/**
 * usePermissions — central composable for role and feature-flag checks.
 *
 * All permission data comes from the `permissions` and `featureFlags` objects
 * embedded in every token response (login, refresh, tenant-switch).
 * Components should import this composable instead of reading from the auth
 * or app store directly.
 *
 * All booleans default to false if the permissions payload has not loaded yet,
 * making this safe to call before the first token refresh completes.
 */
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'

export function usePermissions() {
  const authStore = useAuthStore()
  const { permissions, featureFlags } = storeToRefs(authStore)

  const p = computed(() => permissions.value ?? {})

  // ── Role-level booleans ───────────────────────────────────────────────────

  /** Full administrative access (tenant_admin or superuser) */
  const isAdmin = computed(() => p.value.is_admin === true)

  /** Manager or higher (manager + tenant_admin + superuser) */
  const isManagerOrAbove = computed(() => p.value.is_manager_or_above === true)

  /** Warehouse operator or higher (operator + manager + tenant_admin + superuser) */
  const isOperatorOrAbove = computed(() => p.value.is_operator_or_above === true)

  /**
   * Any non-portal back-office user.
   * True for: tenant_admin, manager, warehouse_operator, user, viewer.
   * False for: portal_user (and unauthenticated).
   */
  const isBackOffice = computed(() => p.value.is_back_office === true)

  // ── Capability flags ──────────────────────────────────────────────────────

  /** Can add, edit, and remove users from the tenant */
  const canManageUsers = computed(() => p.value.can_manage_users === true)

  /** Can create, edit, and delete tenant roles */
  const canManageRoles = computed(() => p.value.can_manage_roles === true)

  /** Can create, edit, and delete locations */
  const canManageLocations = computed(() => p.value.can_manage_locations === true)

  /** Can export tenant data (reports, CSV downloads, etc.) */
  const canExportData = computed(() => p.value.can_export_data === true)

  /** (Portal) Can submit equipment requests via the portal */
  const canRequestEquipment = computed(() => p.value.can_request_equipment === true)

  /** (Portal) Can apply for / accept crew assignments via the portal */
  const canJoinCrew = computed(() => p.value.can_join_crew === true)

  // ── Location access scope ─────────────────────────────────────────────────

  /**
   * The user's location access scope:
   *   'all'      — sees all tenant locations
   *   'assigned' — only explicitly assigned locations
   *   'none'     — no location access
   */
  const locationAccessScope = computed(() => p.value.location_access_scope ?? 'none')

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** The list of system_role strings the user holds (e.g. ['manager']) */
  const systemRoles = computed(() => p.value.system_roles ?? [])

  /**
   * Returns true if the user has the given system role.
   * @param {string} role — e.g. 'tenant_admin', 'manager', 'warehouse_operator'
   */
  function hasRole(role) {
    return systemRoles.value.includes(role)
  }

  /**
   * Returns true if the user has at least one of the given system roles.
   * @param {string[]} roles
   */
  function hasAnyRole(roles) {
    return roles.some((r) => systemRoles.value.includes(r))
  }

  /**
   * Returns true if the tenant feature flag is enabled.
   * @param {string} key — e.g. 'portal_equipment_requests'
   */
  function hasFeature(key) {
    return featureFlags.value?.[key] === true
  }

  return {
    // Role levels
    isAdmin,
    isManagerOrAbove,
    isOperatorOrAbove,
    isBackOffice,
    // Capability flags
    canManageUsers,
    canManageRoles,
    canManageLocations,
    canExportData,
    canRequestEquipment,
    canJoinCrew,
    // Location scope
    locationAccessScope,
    // Helpers
    systemRoles,
    hasRole,
    hasAnyRole,
    hasFeature,
  }
}
